import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import {
  Home, Building2, Ruler, MapPin, Thermometer, CheckCircle2, XCircle,
  ChevronRight, ChevronLeft, Info, ToggleLeft, ToggleRight,
  Hammer, ShieldCheck, Layers, Euro, Calendar
} from "lucide-react";
import { getUserRole } from "@/pages/Login";
import { getUserCommissions, getUserPaymentDelays } from "@/pages/Login";
import { toast } from "sonner";
import { RdvAdminModal } from "@/components/RdvAdminModal";
import {
  formatCurrency, DOCS_PROFIL as SHARED_DOCS_PROFIL, DOCS_PROG as SHARED_DOCS_PROG,
  // Constants
  COEFFICIENTS_SURFACE, CEE_PAR_CLASSES, TVA, COUT_UNITAIRE,
  MONTANT_KWHC, PRIX_RACHAT, MPR_AIDES, TAUX_FINANCEMENT_MPR, MARGE_TABLE,
  DPE_CLASSES, DPE_COLORS, PERIODES, PRECARITE_COLORS, REVENUE_THRESHOLDS,
  GESTES_ISO, GESTES_ISO_MAISON_ONLY, GESTES_ISO_LABELS,
  CHARGES_MPR_DEFAUT, MPR_TIMELINE_STEPS, TIMELINE_174_STEPS,
  // Functions
  getCoefficient, getMPRSurfaceKey, getRegies,
  check174Eligibility, determinePrecarite,
  calcMPR, calc174, calcCout174, calcAutoQuantites174, getScenarios174,
  computeSteps, getSecondProgramSteps, buildCashFlowEvents
} from "@/utils/simulateurUtils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";

import { EligCard } from "./simulateur/EligCard";
import { ResultsStep } from "./simulateur/ResultsStep";

// ═══ COMPOSANT PRINCIPAL ═══
export default function SimulateurTravaux() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const isRegieUser = getUserRole() === "regie";
  const isAdmin = getUserRole() === "admin_full" || getUserRole() === "admin_restricted" || getUserRole() === "administratif";
  const isAdminFull = getUserRole() === "admin_full";
  const isAdminRestricted = getUserRole() === "admin_restricted" || getUserRole() === "administratif";
  const isAdministratifUser = getUserRole() === "administratif";
  const userCommissions = getUserCommissions();
  const userPaymentDelays = getUserPaymentDelays();

  const [profil, setProfil] = useState("");
  const [periode] = useState("from_jan_2026");
  const [periodeMPRAvant, setPeriodeMPRAvant] = useState(false);
  const [typeBatiment, setTypeBatiment] = useState("");
  const [surface, setSurface] = useState(70);
  const [zone, setZone] = useState("H1");
  const [niveaux, setNiveaux] = useState("plain_pied");
  const [chauffage, setChauffage] = useState("");
  const [precarite, setPrecarite] = useState("bleu");
  const [nbPersonnes, setNbPersonnes] = useState(1);
  const [regionFiscale, setRegionFiscale] = useState("province");
  const [revenuAnnuel, setRevenuAnnuel] = useState("");
  const [dpeActuel, setDpeActuel] = useState("");
  const [ageLogement, setAgeLogement] = useState("");
  const [gestesDeja, setGestesDeja] = useState([]);
  const [valorisation, setValorisation] = useState(13);
  const [regieMode, setRegieMode] = useState("regie");
  const [couts, setCouts] = useState(
    Object.fromEntries(Object.entries(COUT_UNITAIRE).map(([k, v]) => [k, { materiel: v.materiel, pose: v.pose }]))
  );
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [selected174Idx, setSelected174Idx] = useState(null);
  const [adminSaving, setAdminSaving] = useState(false);
  const [selectedMPRType, setSelectedMPRType] = useState(null);

  // Multi-logement state (Phase E)
  const [multiLogement, setMultiLogement] = useState(false);
  const [typeMulti, setTypeMulti] = useState(""); // "lotissement" or "immeuble"
  const [nbLogements, setNbLogements] = useState(2);
  const [surfaceMode, setSurfaceMode] = useState("ensemble"); // "ensemble" or "detail"
  const [surfacesDetail, setSurfacesDetail] = useState([]);
  const [chauffageMode, setChauffageMode] = useState("ensemble");
  const [chauffagesDetail, setChauffagesDetail] = useState([]);
  const [dpeMode, setDpeMode] = useState("ensemble");
  const [dpesDetail, setDpesDetail] = useState([]);
  const [precariteMode, setPrecariteMode] = useState("ensemble");
  const [precaritesDetail, setPrecaritesDetail] = useState([]);
  const [gestesMode, setGestesMode] = useState("ensemble");
  const [gestesDejaDetail, setGestesDejaDetail] = useState([]);
  const [ageMode, setAgeMode] = useState("ensemble");
  const [agesDetail, setAgesDetail] = useState([]);

  const isMaison = typeBatiment === "maison";
  const isBailleur = profil === "bailleur_sci";

  // Initialize detail arrays when nbLogements changes
  useEffect(() => {
    if (multiLogement && nbLogements > 0) {
      setSurfacesDetail(prev => {
        const arr = [...prev];
        while (arr.length < nbLogements) arr.push(surface || 70);
        return arr.slice(0, nbLogements);
      });
      setChauffagesDetail(prev => {
        const arr = [...prev];
        while (arr.length < nbLogements) arr.push(chauffage || "Gaz");
        return arr.slice(0, nbLogements);
      });
      setDpesDetail(prev => {
        const arr = [...prev];
        while (arr.length < nbLogements) arr.push(dpeActuel || "D");
        return arr.slice(0, nbLogements);
      });
      setPrecaritesDetail(prev => {
        const arr = [...prev];
        while (arr.length < nbLogements) arr.push("bleu");
        return arr.slice(0, nbLogements);
      });
      setGestesDejaDetail(prev => {
        const arr = [...prev];
        while (arr.length < nbLogements) arr.push([]);
        return arr.slice(0, nbLogements);
      });
      setAgesDetail(prev => {
        const arr = [...prev];
        while (arr.length < nbLogements) arr.push(ageLogement || "more_than_15");
        return arr.slice(0, nbLogements);
      });
    }
  }, [nbLogements, multiLogement]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync global chauffage from detail mode for MPR eligibility
  useEffect(() => {
    if (multiLogement && chauffageMode === "detail" && chauffagesDetail.length > 0) {
      // Use the first logement's chauffage as the reference for global eligibility
      const firstChauffage = chauffagesDetail[0];
      if (firstChauffage && firstChauffage !== chauffage) {
        setChauffage(firstChauffage);
      }
    }
  }, [multiLogement, chauffageMode, chauffagesDetail]); // eslint-disable-line react-hooks/exhaustive-deps

  const isJaune = precarite === "jaune";
  const precariteEligible = precarite === "bleu" || precarite === "jaune";
  const effectiveRegieMode = isRegieUser ? "regie" : regieMode;

  const regies = useMemo(() => getRegies(effectiveRegieMode, precarite, surface, userCommissions), [effectiveRegieMode, precarite, surface, userCommissions]);

  const autoQtes = useMemo(() =>
    calcAutoQuantites174(surface, typeBatiment, niveaux, gestesDeja, chauffage),
    [surface, typeBatiment, niveaux, gestesDeja, chauffage]
  );

  const scenarios174 = useMemo(() =>
    getScenarios174(autoQtes, isMaison, chauffage),
    [autoQtes, isMaison, chauffage]
  );

  const elig174 = useMemo(() => {
    if (!precariteEligible) return { eligible: false, reason: `Précarité ${precarite} non éligible. Seuls les ménages Bleu ou Jaune sont éligibles.` };
    if (scenarios174.length === 0) return { eligible: false, reason: "Pas assez de gestes d'isolation disponibles (minimum 2 requis)." };
    return check174Eligibility(profil, periode, dpeActuel, ageLogement);
  }, [scenarios174, profil, periode, dpeActuel, ageLogement, precariteEligible, precarite]);

  const eligMPR = useMemo(() => {
    if (!precariteEligible) return { eligible: false, reason: `Précarité ${precarite} non éligible. Seuls les ménages Bleu ou Jaune sont éligibles.` };
    if (!isMaison) return { eligible: false, reason: "Le programme MPR n'est disponible que pour les maisons." };
    if (chauffage !== "Gaz" && chauffage !== "Fioul") return { eligible: false, reason: "Le programme MPR nécessite un chauffage au gaz ou au fioul." };
    return { eligible: true, reason: "Éligible au MPR (maison + chauffage gaz/fioul)." };
  }, [isMaison, chauffage, precariteEligible, precarite]);

  // Résultats 174 standalone
  const results174 = useMemo(() => {
    if (scenarios174.length === 0) return [];
    return scenarios174.map(sc => ({
      scenario: sc,
      result: calc174(surface, sc, valorisation, couts, autoQtes, regies.r174, isJaune)
    }));
  }, [scenarios174, surface, valorisation, couts, autoQtes, regies.r174, isJaune]);

  const best174 = useMemo(() => {
    if (results174.length === 0) return null;
    return results174.reduce((best, r) => r.result.marge > best.result.marge ? r : best, results174[0]);
  }, [results174]);

  // Résultats MPR — 3 types de chantier
  const allMPRResults = useMemo(() => {
    if (!eligMPR.eligible) return [];
    return ["PAC", "PAC/SSC", "PAC + CESI"].map(type => ({
      type,
      result: calcMPR(zone, surface, precarite, type, periodeMPRAvant, regies.rMPR)
    }));
  }, [eligMPR.eligible, zone, surface, precarite, periodeMPRAvant, regies.rMPR]);

  const bestMPR = useMemo(() => {
    if (allMPRResults.length === 0) return null;
    return allMPRResults.reduce((best, r) => r.result.marge > best.result.marge ? r : best, allMPRResults[0]);
  }, [allMPRResults]);

  // Actifs (sélectionnés par l'utilisateur ou meilleur par défaut)
  const active174 = selected174Idx !== null && results174[selected174Idx] ? results174[selected174Idx] : best174;
  const activeMPR = selectedMPRType ? allMPRResults.find(r => r.type === selectedMPRType) || bestMPR : bestMPR;

  // Éligibilité Op. Cumulée (doit être avant active174Cumule qui en dépend)
  const eligCumule = useMemo(() => {
    if (!eligMPR.eligible) return { eligible: false, reason: "MPR non éligible : " + eligMPR.reason, order: null };
    if (scenarios174.length === 0) return { eligible: false, reason: "Pas assez de gestes d'isolation pour la 174 (minimum 2 requis).", order: null };
    if (profil === "bailleur_sci") {
      if (!elig174.eligible) return { eligible: false, reason: "174 non éligible : " + elig174.reason, order: null };
      return { eligible: true, reason: "Éligible. Ordre : 174 puis MPR.", order: ["174", "MPR"] };
    }
    if (profil === "proprietaire_occupant") {
      const elig174PostMPR = check174Eligibility("proprietaire_occupant", periode, "D", ageLogement);
      if (!elig174PostMPR.eligible) return { eligible: false, reason: "174 non éligible même après MPR (DPE → D) : " + elig174PostMPR.reason, order: null };
      const scenariosCumule = scenarios174.filter(s => s.sauts <= 2);
      if (scenariosCumule.length === 0) return { eligible: false, reason: "Aucun scénario 174 réalisable avec seulement 2 sauts de classe (DPE D après MPR).", order: null };
      return { eligible: true, reason: "Éligible. Ordre : MPR d'abord (DPE passe à D après PAC/SSC), puis 174 (2 sauts max).", order: ["MPR", "174"], maxSauts: 2 };
    }
    return { eligible: false, reason: "Profil non reconnu pour Op. Cumulée.", order: null };
  }, [profil, periode, ageLogement, eligMPR, elig174, scenarios174]);

  // Résultats 174 avec régie cumulé — utilise le scénario filtré par maxSauts si applicable
  const active174Cumule = useMemo(() => {
    if (!active174 || results174.length === 0) return null;
    let cumScenario = active174.scenario;
    // Si cumulé impose un max de sauts, vérifier que le scénario actif est valide
    if (eligCumule?.maxSauts && cumScenario.sauts > eligCumule.maxSauts) {
      const validResults = results174.filter(r => r.scenario.sauts <= eligCumule.maxSauts);
      if (validResults.length > 0) {
        cumScenario = validResults.reduce((best, r) => {
          const bestRes = calc174(surface, best.scenario, valorisation, couts, autoQtes, regies.r174Cum, isJaune);
          const rRes = calc174(surface, r.scenario, valorisation, couts, autoQtes, regies.r174Cum, isJaune);
          return rRes.marge > bestRes.marge ? r : best;
        }, validResults[0]).scenario;
      }
    }
    return { scenario: cumScenario, result: calc174(surface, cumScenario, valorisation, couts, autoQtes, regies.r174Cum, isJaune) };
  }, [active174, surface, valorisation, couts, autoQtes, regies.r174Cum, isJaune, eligCumule, results174]);

  // MPR actif avec régie cumulé
  const activeMPRCumule = useMemo(() => {
    if (!activeMPR) return null;
    return { type: activeMPR.type, result: calcMPR(zone, surface, precarite, activeMPR.type, periodeMPRAvant, regies.rMPRCum) };
  }, [activeMPR, zone, surface, precarite, periodeMPRAvant, regies.rMPRCum]);

  // Multi-logement multiplier
  const multiN = multiLogement ? nbLogements : 1;

  // Recommandation triée par rentabilité
  const recommendation = useMemo(() => {
    const options = [];
    const hasDetailSurfaces = multiLogement && surfaceMode === "detail" && multiN > 1;

    if (elig174.eligible && active174) {
      let totalMarge, totalCA;
      if (hasDetailSurfaces) {
        let sumM = 0, sumC = 0;
        for (let i = 0; i < nbLogements; i++) {
          const lS = surfacesDetail[i] || surface;
          const lCh = chauffageMode === "detail" && chauffagesDetail[i] ? chauffagesDetail[i] : chauffage;
          const lAQ = calcAutoQuantites174(lS, typeBatiment, niveaux, gestesDeja, lCh);
          const lR = calc174(lS, active174.scenario, valorisation, couts, lAQ, regies.r174, isJaune);
          sumM += lR.marge; sumC += lR.caTTC;
        }
        totalMarge = sumM; totalCA = sumC;
      } else {
        totalMarge = active174.result.marge * multiN; totalCA = active174.result.caTTC * multiN;
      }
      options.push({ type: "174", label: "Programme 174", marge: totalMarge, ca: totalCA, margeUnit: totalMarge / multiN, caUnit: totalCA / multiN });
    }
    if (eligMPR.eligible && activeMPR) {
      let totalMarge, totalCA;
      if (hasDetailSurfaces) {
        let sumM = 0, sumC = 0;
        for (let i = 0; i < nbLogements; i++) {
          const lS = surfacesDetail[i] || surface;
          const lR = calcMPR(zone, lS, precarite, activeMPR.type, periodeMPRAvant, regies.rMPR);
          sumM += lR.marge; sumC += lR.totalAides;
        }
        totalMarge = sumM; totalCA = sumC;
      } else {
        totalMarge = activeMPR.result.marge * multiN; totalCA = activeMPR.result.totalAides * multiN;
      }
      options.push({ type: "mpr", label: `Programme MPR (${activeMPR.type})`, marge: totalMarge, ca: totalCA, margeUnit: totalMarge / multiN, caUnit: totalCA / multiN });
    }
    if (eligCumule.eligible && active174Cumule && activeMPRCumule) {
      const orderLabel = eligCumule.order.join(" puis ");
      let totalMarge, totalCA;
      if (hasDetailSurfaces) {
        let s174M = 0, s174C = 0, sMprM = 0, sMprC = 0;
        for (let i = 0; i < nbLogements; i++) {
          const lS = surfacesDetail[i] || surface;
          const lCh = chauffageMode === "detail" && chauffagesDetail[i] ? chauffagesDetail[i] : chauffage;
          const lAQ = calcAutoQuantites174(lS, typeBatiment, niveaux, gestesDeja, lCh);
          const l174 = calc174(lS, active174Cumule.scenario, valorisation, couts, lAQ, regies.r174Cum, isJaune);
          const lMpr = calcMPR(zone, lS, precarite, activeMPRCumule.type, periodeMPRAvant, regies.rMPRCum);
          s174M += l174.marge; s174C += l174.caTTC;
          sMprM += lMpr.marge; sMprC += lMpr.totalAides;
        }
        totalMarge = s174M + sMprM; totalCA = s174C + sMprC;
      } else {
        const mUnit = active174Cumule.result.marge + activeMPRCumule.result.marge;
        const cUnit = active174Cumule.result.caTTC + activeMPRCumule.result.totalAides;
        totalMarge = mUnit * multiN; totalCA = cUnit * multiN;
      }
      options.push({
        type: "cumule", label: `Op. Cumulée (${orderLabel})`,
        marge: totalMarge, ca: totalCA,
        margeUnit: totalMarge / multiN, caUnit: totalCA / multiN
      });
    }
    return options.sort((a, b) => b.marge - a.marge);
  }, [elig174.eligible, eligMPR.eligible, eligCumule, active174, activeMPR, active174Cumule, activeMPRCumule, multiN, multiLogement, surfaceMode, surfacesDetail, surface, chauffageMode, chauffagesDetail, chauffage, nbLogements, typeBatiment, niveaux, gestesDeja, valorisation, couts, regies, zone, precarite, periodeMPRAvant, isJaune]);

  const steps = [
    { id: "profil", label: "Profil client" },
    { id: "logement", label: "Logement" },
    { id: "dpe", label: "DPE & Précarité" },
    { id: "isolation", label: "Travaux existants" },
    { id: "resultats", label: "Résultats" }
  ];

  const canNext = () => {
    switch (step) {
      case 0: return !!profil;
      case 1: {
        if (multiLogement) {
          return !!typeMulti && nbLogements >= 2 && !!zone && (chauffageMode === "ensemble" ? !!chauffage : chauffagesDetail.every(c => !!c));
        }
        return !!typeBatiment && surface > 0 && !!zone && !!chauffage;
      }
      case 2: {
        if (multiLogement) {
          const dpeOK = dpeMode === "ensemble" ? !!dpeActuel : dpesDetail.every(d => !!d);
          const ageOK = ageMode === "ensemble" ? !!ageLogement : agesDetail.every(a => !!a);
          return dpeOK && ageOK;
        }
        return !!dpeActuel && !!ageLogement && !!revenuAnnuel && precariteEligible;
      }
      default: return true;
    }
  };

  const toggleGeste = (g) => {
    setGestesDeja(prev => {
      const isAdding = !prev.includes(g);
      let next = isAdding ? [...prev, g] : prev.filter(x => x !== g);
      // Combles et rampant sont liés : si l'un est coché, l'autre aussi
      if (g === "combles" || g === "rampant") {
        const linked = g === "combles" ? "rampant" : "combles";
        if (isAdding && !next.includes(linked)) next = [...next, linked];
        if (!isAdding) next = next.filter(x => x !== linked);
      }
      return next;
    });
  };

  const gestesForBuilding = GESTES_ISO.filter(g => {
    if (!isMaison && GESTES_ISO_MAISON_ONLY.includes(g)) return false;
    return true;
  });

  return (
    <div className="space-y-6 max-w-6xl">
      <div>
        <h1 className="font-heading text-2xl font-bold flex items-center gap-3">
          <Hammer className="w-7 h-7 text-[hsl(var(--renova-green))]" />
          Simulateur / Nouveau dossier
        </h1>
        <p className="text-muted-foreground mt-1">Analysez l'éligibilité et la rentabilité des programmes pour votre client.</p>
      </div>

      {/* Progress */}
      <div className="flex items-center gap-1">
        {steps.map((s, i) => (
          <div key={s.id} className="flex items-center flex-1">
            <button
              onClick={() => i <= step && setStep(i)}
              className={cn(
                "flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all w-full",
                i === step ? "bg-[hsl(var(--renova-green))] text-white shadow-md" :
                  i < step ? "bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 cursor-pointer hover:bg-emerald-200" :
                    "bg-muted text-muted-foreground"
              )}
            >
              <span className={cn("w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold",
                i === step ? "bg-white/20 text-white" : i < step ? "bg-emerald-600 text-white" : "bg-border text-muted-foreground"
              )}>
                {i < step ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
              </span>
              <span className="hidden md:inline">{s.label}</span>
            </button>
            {i < steps.length - 1 && <ChevronRight className="w-4 h-4 text-muted-foreground mx-1 shrink-0" />}
          </div>
        ))}
      </div>

      {/* Step 0: Profil */}
      {step === 0 && (
        <div className="border border-border rounded-lg bg-card p-8 space-y-6">
          <h2 className="font-semibold text-lg flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-[hsl(var(--renova-green))]" /> Quel est le profil du bénéficiaire ?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { id: "bailleur_sci", label: "Bailleur en SCI", icon: Building2 },
              { id: "proprietaire_occupant", label: "Propriétaire occupant", icon: Home },
            ].map(p => (
              <button key={p.id} onClick={() => setProfil(p.id)}
                className={cn("p-6 rounded-lg border-2 transition-all text-left flex items-center gap-4",
                  profil === p.id ? "border-[hsl(var(--renova-green))] bg-emerald-500/10 shadow-md" : "border-border hover:border-emerald-500/40"
                )} data-testid={`profil-${p.id}`}>
                <p.icon className={cn("w-8 h-8", profil === p.id ? "text-[hsl(var(--renova-green))]" : "text-muted-foreground")} />
                <span className="font-medium">{p.label}</span>
              </button>
            ))}
          </div>
          {/* Switch Période MPR - Admin only */}
          {isAdmin && (
            <div className="mt-4 pt-6 border-t border-border">
              <h2 className="font-semibold text-base flex items-center gap-2 mb-4"><Calendar className="w-5 h-5 text-[hsl(var(--renova-green))]" /> Période MPR</h2>
              <button type="button" onClick={() => setPeriodeMPRAvant(!periodeMPRAvant)}
                className={cn("flex items-center gap-4 w-full p-4 rounded-lg border-2 transition-all", periodeMPRAvant ? "border-amber-400 bg-amber-500/10" : "border-border bg-card")}
                data-testid="switch-periode-mpr">
                {periodeMPRAvant ? <ToggleRight className="w-7 h-7 text-amber-500" /> : <ToggleLeft className="w-7 h-7 text-muted-foreground" />}
                <div className="text-left flex-1">
                  <p className="text-sm font-semibold">{periodeMPRAvant ? "Avant 31/12/25" : "Après 31/12/25"}</p>
                </div>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Step 1: Logement */}
      {step === 1 && (
        <div className="border border-border rounded-lg bg-card p-8 space-y-6">
          <h2 className="font-semibold text-lg flex items-center gap-2"><Home className="w-5 h-5 text-[hsl(var(--renova-green))]" /> Informations sur le logement</h2>

          {/* Multi-logement choice for Bailleur SCI */}
          {isBailleur && (
            <div className="space-y-4">
              <Label className="font-semibold">Nombre de logements</Label>
              <div className="flex gap-3">
                <button onClick={() => { setMultiLogement(false); setTypeMulti(""); }}
                  className={cn("flex-1 p-4 rounded-lg border-2 text-sm font-medium transition-all",
                    !multiLogement ? "border-[hsl(var(--renova-green))] bg-emerald-500/10" : "border-border hover:border-emerald-500/40"
                  )} data-testid="btn-1-logement">
                  1 logement
                </button>
                <button onClick={() => setMultiLogement(true)}
                  className={cn("flex-1 p-4 rounded-lg border-2 text-sm font-medium transition-all",
                    multiLogement ? "border-[hsl(var(--renova-green))] bg-emerald-500/10" : "border-border hover:border-emerald-500/40"
                  )} data-testid="btn-multi-logement">
                  Plusieurs logements
                </button>
              </div>
              {multiLogement && (
                <div className="space-y-4 p-4 border border-emerald-500/30 bg-emerald-500/10 rounded-lg">
                  <div>
                    <Label className="font-semibold">Type</Label>
                    <div className="flex gap-3 mt-2">
                      <button onClick={() => { setTypeMulti("lotissement"); setTypeBatiment("maison"); }}
                        className={cn("flex-1 p-3 rounded-lg border-2 text-sm font-medium transition-all",
                          typeMulti === "lotissement" ? "border-[hsl(var(--renova-green))] bg-emerald-500/10" : "border-border hover:border-emerald-500/40"
                        )} data-testid="type-lotissement">
                        Lotissement (maisons)
                      </button>
                      <button onClick={() => { setTypeMulti("immeuble"); setTypeBatiment("appartement"); }}
                        className={cn("flex-1 p-3 rounded-lg border-2 text-sm font-medium transition-all",
                          typeMulti === "immeuble" ? "border-[hsl(var(--renova-green))] bg-emerald-500/10" : "border-border hover:border-emerald-500/40"
                        )} data-testid="type-immeuble">
                        Immeuble (appartements)
                      </button>
                    </div>
                  </div>
                  <div>
                    <Label>Nombre de logements</Label>
                    <Input type="number" min="2" max="100" value={nbLogements} onChange={e => setNbLogements(parseInt(e.target.value) || 2)}
                      className="mt-2 w-32" data-testid="input-nb-logements" />
                  </div>
                  <div>
                    <Label>Surfaces</Label>
                    <div className="flex gap-3 mt-2">
                      <button onClick={() => setSurfaceMode("ensemble")}
                        className={cn("px-4 py-2 rounded-lg border-2 text-xs font-medium",
                          surfaceMode === "ensemble" ? "border-[hsl(var(--renova-green))] bg-emerald-500/10" : "border-border"
                        )} data-testid="surface-ensemble">Surface moyenne</button>
                      <button onClick={() => setSurfaceMode("detail")}
                        className={cn("px-4 py-2 rounded-lg border-2 text-xs font-medium",
                          surfaceMode === "detail" ? "border-[hsl(var(--renova-green))] bg-emerald-500/10" : "border-border"
                        )} data-testid="surface-detail">Détail par logement</button>
                    </div>
                    {surfaceMode === "ensemble" ? (
                      <Input type="number" value={surface} onChange={e => setSurface(parseInt(e.target.value) || 0)} className="mt-2 w-40" placeholder="Surface moyenne" />
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                        {surfacesDetail.map((s, i) => (
                          <div key={i} className="flex items-center gap-1">
                            <span className="text-xs text-muted-foreground w-8">L{i + 1}</span>
                            <Input type="number" value={s} onChange={e => {
                              const arr = [...surfacesDetail]; arr[i] = parseInt(e.target.value) || 0; setSurfacesDetail(arr);
                            }} className="h-8 text-xs" />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Standard fields */}
          {!multiLogement && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label>Type de bâtiment *</Label>
                <div className="flex gap-3 mt-2">
                  {[{ id: "maison", label: "Maison" }, { id: "appartement", label: "Appartement" }].map(t => (
                    <button key={t.id} onClick={() => setTypeBatiment(t.id)}
                      className={cn("flex-1 p-4 rounded-lg border-2 text-sm font-medium transition-all",
                        typeBatiment === t.id ? "border-[hsl(var(--renova-green))] bg-emerald-500/10" : "border-border hover:border-emerald-500/40"
                      )} data-testid={`type-${t.id}`}>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Surface totale (m²) *</Label>
                <Input type="number" value={surface} onChange={e => setSurface(parseInt(e.target.value) || 0)} className="mt-2" data-testid="input-surface" />
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label>Zone climatique *</Label>
              <Select value={zone} onValueChange={setZone}>
                <SelectTrigger className="mt-2" data-testid="select-zone"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="H1">H1 (Nord)</SelectItem>
                  <SelectItem value="H2">H2 (Ouest)</SelectItem>
                  <SelectItem value="H3">H3 (Sud)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Chauffage actuel *</Label>
              {multiLogement ? (
                <div className="space-y-2 mt-2">
                  <div className="flex gap-2">
                    <button onClick={() => setChauffageMode("ensemble")}
                      className={cn("px-3 py-1 rounded-lg border text-xs", chauffageMode === "ensemble" ? "border-[hsl(var(--renova-green))] bg-emerald-500/10" : "border-border")}
                    >Ensemble</button>
                    <button onClick={() => setChauffageMode("detail")}
                      className={cn("px-3 py-1 rounded-lg border text-xs", chauffageMode === "detail" ? "border-[hsl(var(--renova-green))] bg-emerald-500/10" : "border-border")}
                    >Détail</button>
                  </div>
                  {chauffageMode === "ensemble" ? (
                    <div className="flex gap-2 flex-wrap">
                      {["Gaz", "Fioul", "Électrique"].map(c => (
                        <button key={c} onClick={() => setChauffage(c)}
                          className={cn("px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all",
                            chauffage === c ? "border-[hsl(var(--renova-green))] bg-emerald-500/10" : "border-border"
                          )} data-testid={`chauffage-${c}`}><Thermometer className="w-4 h-4 inline mr-1" />{c}</button>
                      ))}
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-1">
                      {chauffagesDetail.map((ch, i) => (
                        <div key={i} className="flex items-center gap-1">
                          <span className="text-xs text-muted-foreground w-8">L{i + 1}</span>
                          <select value={ch} onChange={e => { const arr = [...chauffagesDetail]; arr[i] = e.target.value; setChauffagesDetail(arr); }}
                            className="text-xs px-2 py-1 rounded border border-border bg-background">
                            <option value="Gaz">Gaz</option><option value="Fioul">Fioul</option><option value="Électrique">Élec.</option>
                          </select>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex gap-2 mt-2 flex-wrap">
                  {["Gaz", "Fioul", "Électrique"].map(c => (
                    <button key={c} onClick={() => setChauffage(c)}
                      className={cn("px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all",
                        chauffage === c ? "border-[hsl(var(--renova-green))] bg-emerald-500/10" : "border-border hover:border-emerald-500/40"
                      )} data-testid={`chauffage-${c}`}>
                      <Thermometer className="w-4 h-4 inline mr-1" />{c}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {isMaison && !multiLogement && (
              <div>
                <Label>Niveaux de la maison</Label>
                <div className="flex gap-3 mt-2">
                  {[{ id: "plain_pied", label: "Plain-pied" }, { id: "1_niveau", label: "1 niveau (R+1)" }].map(n => (
                    <button key={n.id} onClick={() => setNiveaux(n.id)}
                      className={cn("flex-1 p-3 rounded-lg border-2 text-sm font-medium transition-all",
                        niveaux === n.id ? "border-[hsl(var(--renova-green))] bg-emerald-500/10" : "border-border hover:border-emerald-500/40"
                      )} data-testid={`niveaux-${n.id}`}>
                      {n.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Step 2: DPE & Précarité */}
      {step === 2 && (
        <div className="border border-border rounded-lg bg-card p-8 space-y-6">
          <h2 className="font-semibold text-lg flex items-center gap-2"><Layers className="w-5 h-5 text-[hsl(var(--renova-green))]" /> DPE, Précarité et Revenus</h2>

          {/* DPE - with multi-logement support */}
          {multiLogement ? (
            <div className="space-y-6">
              <div className="border border-border rounded-lg p-5 bg-muted/20">
                <Label className="font-semibold text-base">DPE des logements</Label>
                <div className="flex gap-2 mt-2">
                  <button onClick={() => setDpeMode("ensemble")} className={cn("px-3 py-1 rounded-lg border text-xs", dpeMode === "ensemble" ? "border-[hsl(var(--renova-green))] bg-emerald-500/10" : "border-border")}>Ensemble</button>
                  <button onClick={() => setDpeMode("detail")} className={cn("px-3 py-1 rounded-lg border text-xs", dpeMode === "detail" ? "border-[hsl(var(--renova-green))] bg-emerald-500/10" : "border-border")}>Détail</button>
                </div>
              {dpeMode === "ensemble" ? (
                <div className="flex gap-2 flex-wrap">
                  {DPE_CLASSES.map(d => (
                    <button key={d} onClick={() => setDpeActuel(d)}
                      className={cn("w-12 h-12 rounded-lg text-white font-bold text-lg transition-all border-2",
                        DPE_COLORS[d], dpeActuel === d ? "border-foreground ring-2 ring-emerald-500 scale-110" : "border-transparent opacity-70 hover:opacity-100"
                      )} data-testid={`dpe-${d}`}>{d}</button>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {dpesDetail.map((dp, i) => (
                    <div key={i} className="flex items-center gap-1">
                      <span className="text-xs text-muted-foreground w-8">L{i + 1}</span>
                      <select value={dp} onChange={e => { const arr = [...dpesDetail]; arr[i] = e.target.value; setDpesDetail(arr); }}
                        className="text-xs px-2 py-1 rounded border border-border bg-background">
                        {DPE_CLASSES.map(d => <option key={d} value={d}>{d}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              )}

              </div>

              <div className="border border-border rounded-lg p-5 bg-muted/20">
              <Label className="font-semibold text-base">Âge du logement</Label>
              <div className="flex gap-2">
                <button onClick={() => setAgeMode("ensemble")} className={cn("px-3 py-1 rounded-lg border text-xs", ageMode === "ensemble" ? "border-[hsl(var(--renova-green))] bg-emerald-500/10" : "border-border")}>Ensemble</button>
                <button onClick={() => setAgeMode("detail")} className={cn("px-3 py-1 rounded-lg border text-xs", ageMode === "detail" ? "border-[hsl(var(--renova-green))] bg-emerald-500/10" : "border-border")}>Détail</button>
              </div>
              {ageMode === "ensemble" ? (
                <select value={ageLogement} onChange={e => setAgeLogement(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-border bg-background text-sm" data-testid="select-age-multi">
                  <option value="">Sélectionnez...</option>
                  <option value="less_than_2">Moins de 2 ans</option>
                  <option value="2_to_15">Entre 2 et 15 ans</option>
                  <option value="more_than_15">Plus de 15 ans</option>
                </select>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {agesDetail.map((ag, i) => (
                    <div key={i} className="flex items-center gap-1">
                      <span className="text-xs text-muted-foreground w-8">L{i + 1}</span>
                      <select value={ag} onChange={e => { const arr = [...agesDetail]; arr[i] = e.target.value; setAgesDetail(arr); }}
                        className="text-xs px-2 py-1 rounded border border-border bg-background">
                        <option value="less_than_2">&lt;2 ans</option><option value="2_to_15">2-15 ans</option><option value="more_than_15">&gt;15 ans</option>
                      </select>
                    </div>
                  ))}
                </div>
              )}

              </div>

              <div className="border border-border rounded-lg p-5 bg-muted/20">
              <Label className="font-semibold text-base">Précarité des locataires</Label>
              <p className="text-xs text-muted-foreground">Seuls les Bleu sont éligibles en 174 multi-logements.</p>
              <div className="flex gap-2">
                <button onClick={() => setPrecariteMode("ensemble")} className={cn("px-3 py-1 rounded-lg border text-xs", precariteMode === "ensemble" ? "border-[hsl(var(--renova-green))] bg-emerald-500/10" : "border-border")}>Ensemble</button>
                <button onClick={() => setPrecariteMode("detail")} className={cn("px-3 py-1 rounded-lg border text-xs", precariteMode === "detail" ? "border-[hsl(var(--renova-green))] bg-emerald-500/10" : "border-border")}>Détail</button>
              </div>
              {precariteMode === "ensemble" ? (
                <select value={precarite} onChange={e => { setPrecarite(e.target.value); setValorisation(e.target.value === "bleu" ? 13 : 8); }}
                  className="px-3 py-2 rounded-lg border border-border bg-background text-sm" data-testid="select-precarite-multi">
                  <option value="bleu">Bleu (Très modeste) - Éligible</option>
                  <option value="jaune">Jaune (Modeste) - Non éligible</option>
                  <option value="violet">Violet - Non éligible</option>
                  <option value="rose">Rose - Non éligible</option>
                </select>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {precaritesDetail.map((pc, i) => (
                    <div key={i} className="flex items-center gap-1">
                      <span className="text-xs text-muted-foreground w-8">L{i + 1}</span>
                      <select value={pc} onChange={e => { const arr = [...precaritesDetail]; arr[i] = e.target.value; setPrecaritesDetail(arr); }}
                        className="text-xs px-2 py-1 rounded border border-border bg-background">
                        <option value="bleu">Bleu</option><option value="jaune">Jaune</option><option value="violet">Violet</option><option value="rose">Rose</option>
                      </select>
                    </div>
                  ))}
                </div>
              )}

              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label>DPE actuel du logement *</Label>
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {DPE_CLASSES.map(d => (
                      <button key={d} onClick={() => setDpeActuel(d)}
                        className={cn("w-12 h-12 rounded-lg text-white font-bold text-lg transition-all border-2",
                          DPE_COLORS[d], dpeActuel === d ? "border-foreground ring-2 ring-emerald-500 scale-110" : "border-transparent opacity-70 hover:opacity-100"
                        )} data-testid={`dpe-${d}`}>
                        {d}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label>Âge du logement *</Label>
                  <Select value={ageLogement} onValueChange={setAgeLogement}>
                    <SelectTrigger className="mt-2" data-testid="select-age"><SelectValue placeholder="Sélectionnez..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="less_than_2">Moins de 2 ans</SelectItem>
                      <SelectItem value="2_to_15">Entre 2 et 15 ans</SelectItem>
                      <SelectItem value="more_than_15">Plus de 15 ans</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}

          {/* Revenus du ménage */}
          <div className="border border-border rounded-lg p-5 space-y-4 bg-muted/30">
            <h3 className="font-semibold text-sm flex items-center gap-2">
              <Euro className="w-4 h-4 text-[hsl(var(--renova-green))]" />
              Revenus du {profil === "bailleur_sci" ? "locataire" : "propriétaire occupant"} *
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs">Nombre de personnes dans le ménage</Label>
                <Input type="number" min={1} value={nbPersonnes} onChange={e => {
                  const val = Math.max(1, parseInt(e.target.value) || 1);
                  setNbPersonnes(val);
                  if (revenuAnnuel) {
                    const auto = determinePrecarite(val, regionFiscale, parseFloat(revenuAnnuel));
                    if (auto) { setPrecarite(auto); setValorisation(auto === "jaune" ? 8 : 13); }
                  }
                }} className="mt-1" data-testid="input-nb-personnes" />
              </div>
              <div>
                <Label className="text-xs">Région fiscale</Label>
                <Select value={regionFiscale} onValueChange={v => {
                  setRegionFiscale(v);
                  if (revenuAnnuel) {
                    const auto = determinePrecarite(nbPersonnes, v, parseFloat(revenuAnnuel));
                    if (auto) { setPrecarite(auto); setValorisation(auto === "jaune" ? 8 : 13); }
                  }
                }}>
                  <SelectTrigger className="mt-1" data-testid="select-region-fiscale"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="idf">Île-de-France</SelectItem>
                    <SelectItem value="province">Province (hors ÎdF)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Revenu fiscal annuel (€)</Label>
                <Input type="number" value={revenuAnnuel} onChange={e => {
                  const val = e.target.value;
                  setRevenuAnnuel(val);
                  const auto = determinePrecarite(nbPersonnes, regionFiscale, parseFloat(val));
                  if (auto) { setPrecarite(auto); setValorisation(auto === "jaune" ? 8 : 13); }
                }} placeholder="Ex: 25 000" className="mt-1" data-testid="input-revenu" />
              </div>
            </div>
            {precarite && (
              <div className={cn("flex items-center gap-3 p-3 rounded-lg border-2",
                precariteEligible ? "border-emerald-500/40 bg-emerald-500/10" : "border-red-300 bg-red-500/10"
              )} data-testid="precarite-result">
                <span className={cn("w-5 h-5 rounded-full shrink-0", PRECARITE_COLORS[precarite]?.bg)} />
                <div>
                  <span className="font-semibold text-sm">{PRECARITE_COLORS[precarite]?.label}</span>
                  {!precariteEligible && (
                    <p className="text-xs text-red-600 mt-0.5">Non éligible aux programmes. Seuls les ménages Bleu ou Jaune sont éligibles.</p>
                  )}
                  {isJaune && precariteEligible && (
                    <p className="text-xs text-yellow-700 mt-0.5">Jaune (Modeste)</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Step 3: Travaux existants */}
      {step === 3 && (
        <div className="border border-border rounded-lg bg-card p-8 space-y-6">
          <h2 className="font-semibold text-lg flex items-center gap-2"><Hammer className="w-5 h-5 text-[hsl(var(--renova-green))]" /> Travaux d'isolation déjà réalisés</h2>
          <p className="text-sm text-muted-foreground">Cochez les gestes d'isolation qui ont <strong>déjà été réalisés</strong> sur le logement. Pour être éligible à la 174, il faut au minimum 2 gestes d'isolation encore faisables.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {gestesForBuilding.map(g => (
              <button key={g} onClick={() => toggleGeste(g)}
                className={cn("p-4 rounded-lg border-2 text-left flex items-center gap-3 transition-all",
                  gestesDeja.includes(g) ? "border-red-400 bg-red-500/10" : "border-emerald-500/40 bg-emerald-500/10"
                )} data-testid={`geste-${g}`}>
                {gestesDeja.includes(g) ? (
                  <XCircle className="w-6 h-6 text-red-500 shrink-0" />
                ) : (
                  <CheckCircle2 className="w-6 h-6 text-emerald-500 shrink-0" />
                )}
                <div>
                  <p className="font-medium text-sm">{GESTES_ISO_LABELS[g]}</p>
                  <p className="text-xs text-muted-foreground">
                    {gestesDeja.includes(g) ? "Déjà réalisé" : `Disponible — ${autoQtes[g]} ${COUT_UNITAIRE[g]?.unite || ""}`}
                  </p>
                </div>
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/50 border border-border">
            <Info className="w-5 h-5 text-[hsl(var(--renova-green))] shrink-0" />
            <div className="text-sm space-y-1">
              <p>
                <strong>{gestesForBuilding.filter(g => !gestesDeja.includes(g) && autoQtes[g] > 0).length}</strong> geste(s) d'isolation disponible(s) sur {gestesForBuilding.length}.
                {scenarios174.length > 0 ? (
                  <span className="text-emerald-600 font-medium ml-1">Minimum 2 gestes OK pour la 174.</span>
                ) : (
                  <span className="text-red-600 font-medium ml-1">Moins de 2 gestes : non éligible 174.</span>
                )}
              </p>
              {elig174.eligible && <p className="text-emerald-600">174 standalone : {elig174.reason}</p>}
              {!elig174.eligible && eligCumule.eligible && (
                <p className="text-violet-600 font-medium">
                  Op. Cumulée possible : {eligCumule.reason}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Step 4: Résultats */}
      {step === 4 && (
        <ResultsStep
          recommendation={recommendation}
          selectedDetail={selectedDetail}
          setSelectedDetail={setSelectedDetail}
          elig174={elig174}
          eligMPR={eligMPR}
          eligCumule={eligCumule}
          results174={results174}
          best174={best174}
          active174={active174}
          allMPRResults={allMPRResults}
          bestMPR={bestMPR}
          activeMPR={activeMPR}
          active174Cumule={active174Cumule}
          activeMPRCumule={activeMPRCumule}
          autoQtes={autoQtes}
          couts={couts}
          valorisation={valorisation}
          setValorisation={setValorisation}
          regieMode={effectiveRegieMode}
          setRegieMode={setRegieMode}
          regies={regies}
          selected174Idx={selected174Idx}
          setSelected174Idx={setSelected174Idx}
          selectedMPRType={selectedMPRType}
          setSelectedMPRType={setSelectedMPRType}
          zone={zone}
          surface={surface}
          precarite={precarite}
          isJaune={isJaune}
          periode={periode}
          periodeMPRAvant={periodeMPRAvant}
          navigate={navigate}
          isRegieUser={isRegieUser}
          profil={profil}
          typeBatiment={typeBatiment}
          chauffage={chauffage}
          niveaux={niveaux}
          dpeActuel={dpeActuel}
          ageLogement={ageLogement}
          nbPersonnes={nbPersonnes}
          regionFiscale={regionFiscale}
          revenuFiscal={revenuAnnuel}
          gestesDeja={gestesDeja}
          multiN={multiN}
          multiLogement={multiLogement}
          nbLogements={nbLogements}
          surfaceMode={surfaceMode}
          surfacesDetail={surfacesDetail}
          chauffageMode={chauffageMode}
          chauffagesDetail={chauffagesDetail}
          dpeMode={dpeMode}
          dpesDetail={dpesDetail}
          ageMode={ageMode}
          agesDetail={agesDetail}
          precariteMode={precariteMode}
          precaritesDetail={precaritesDetail}
          isAdminFull={isAdminFull}
          isAdminRestricted={isAdminRestricted}
          adminSaving={adminSaving}
          setAdminSaving={setAdminSaving}
        />
      )}

      {/* Navigation */}
      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={() => setStep(s => s - 1)} disabled={step === 0} className="rounded-lg" data-testid="btn-prev">
          <ChevronLeft className="w-4 h-4 mr-2" /> Précédent
        </Button>
        {step < steps.length - 1 && (
          <Button onClick={() => setStep(s => s + 1)} disabled={!canNext()} className="rounded-lg bg-[hsl(var(--renova-green))] hover:bg-[hsl(var(--renova-green-hover))]" data-testid="btn-next">
            Suivant <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        )}
      </div>
    </div>
  );
}

